import React, { useEffect, useState } from 'react';
import { Button, Select, Checkbox, message, Space, Spin } from 'antd';
import { useDispatch, useSelector } from "react-redux";
import {
    createOrUpdateScheduleThunk,
    deleteScheduleThunk,
    getJobScheduleThunk,
    triggerExecutionThunk
} from "../Feature/JobSchedulingSlice";

const NOTIFICATION_FREQUENCIES = [
    { label: 'Never', value: 'NEVER' },
    { label: 'Weekly', value: 'WEEKLY' },
    { label: '3 times per week', value: 'THREE_TIMES_WEEK' },
    { label: 'Daily', value: 'DAILY' }
];

const WEEKLY_AVAILABILITY_DAYS = [
    { label: 'Never', value: 'NEVER' },
    { label: 'Monday', value: 'MONDAY' },
    { label: 'Tuesday', value: 'TUESDAY' },
    { label: 'Wednesday', value: 'WEDNESDAY' },
    { label: 'Thursday', value: 'THURSDAY' },
    { label: 'Friday', value: 'FRIDAY' },
    { label: 'Saturday', value: 'SATURDAY' },
    { label: 'Sunday', value: 'SUNDAY' }
];

const JobConfigurationForm = ({ spaceId, jobType }) => {
    const dispatch = useDispatch();
    const { schedules, triggering } = useSelector((state) => state.jobScheduling);
    const currentSchedule = schedules[jobType];
    const [loading, setLoading] = useState(false);
    const [enabled, setEnabled] = useState(true);
    const [frequency, setFrequency] = useState('NEVER');
    const [day, setDay] = useState('NEVER');

    useEffect(() => {
        if (jobType && spaceId) {
            dispatch(getJobScheduleThunk({ spaceId, jobType }));
        }
    }, [dispatch, spaceId, jobType]);

    useEffect(() => {
        if (currentSchedule) {
            const enabled = currentSchedule.enabled !== undefined ? currentSchedule.enabled : true;
            setEnabled(enabled);

            // Handle both camelCase and snake_case property names
            if (jobType === 'NOTIFICATION_DIGEST') {
                const freq = currentSchedule.notificationFrequency || currentSchedule.notification_frequency || 'NEVER';
                setFrequency(freq);
            } else if (jobType === 'WEEKLY_AVAILABILITY') {
                const day = currentSchedule.weeklyAvailabilityDay || currentSchedule.weekly_availability_day || 'NEVER';
                setDay(day);
            }
        } else {
            setEnabled(true);
            setFrequency('NEVER');
            setDay('NEVER');
        }
    }, [currentSchedule, jobType]);

    const handleSave = async () => {
        setLoading(true);
        try {
            let data;
            if (jobType === 'NOTIFICATION_DIGEST') {
                data = { frequency, enabled };
            } else if (jobType === 'WEEKLY_AVAILABILITY') {
                data = { day, enabled };
            }

            await dispatch(createOrUpdateScheduleThunk({
                spaceId,
                jobType,
                ...data
            }));
            message.success('Configuration saved successfully');
        } catch (error) {
            console.error('Error saving configuration:', error);
            message.error('Failed to save configuration: ' + (error.message || JSON.stringify(error)));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!currentSchedule) {
            message.info('No configuration set');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this configuration?')) {
            return;
        }

        setLoading(true);
        try {
            await dispatch(deleteScheduleThunk({ spaceId, jobType }));
            if (jobType === 'NOTIFICATION_DIGEST') {
                setFrequency('NEVER');
            } else {
                setDay('NEVER');
            }
            setEnabled(true);
            message.success('Configuration deleted successfully');
        } catch (error) {
            message.error('Failed to delete configuration');
        } finally {
            setLoading(false);
        }
    };

    const handleTriggerNow = async () => {
        console.log('Triggering job:', jobType, 'for space:', spaceId);
        try {
            await dispatch(triggerExecutionThunk({ spaceId, jobType }));
            message.success(`${jobType} job triggered successfully`);
        } catch (error) {
            console.error('Error triggering job:', error);
            message.error('Failed to trigger job: ' + (error.message || JSON.stringify(error)));
        }
    };

    if (!jobType) {
        return <div style={{ color: 'red' }}>Error: Job type is not defined</div>;
    }

    const isNotificationDigest = jobType === 'NOTIFICATION_DIGEST';
    const isWeeklyAvailability = jobType === 'WEEKLY_AVAILABILITY';

    return (
        <Spin spinning={loading}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {isNotificationDigest && (
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ marginBottom: '8px', display: 'block' }}>
                            Notification Frequency <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Select
                            value={frequency}
                            onChange={setFrequency}
                            options={NOTIFICATION_FREQUENCIES}
                            style={{ width: '100%' }}
                        />
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                            How often you want to receive notification digest emails.
                        </div>
                    </div>
                )}

                {isWeeklyAvailability && (
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ marginBottom: '8px', display: 'block' }}>
                            Send Weekly Availability On <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Select
                            value={day}
                            onChange={setDay}
                            options={WEEKLY_AVAILABILITY_DAYS}
                            style={{ width: '100%' }}
                        />
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                            Which day of the week to send the weekly availability reminder email.
                        </div>
                    </div>
                )}

                <div style={{ marginBottom: '12px' }}>
                    <Checkbox
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                    >
                        Enable this schedule
                    </Checkbox>
                </div>

                <div>
                    <Space>
                        <Button type="primary" onClick={handleSave} loading={loading}>
                            Save Configuration
                        </Button>
                        <Button onClick={handleTriggerNow} loading={triggering}>
                            Trigger Now
                        </Button>
                        <Button danger onClick={handleDelete}>
                            Delete Configuration
                        </Button>
                    </Space>
                </div>
            </div>
        </Spin>
    );
};

export default JobConfigurationForm;
